import { Component, Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

/*
 * The fax PDF viewer (Inbox > Faxes and Communication > Fax, OSA too).
 *
 * It used to be an <iframe> on the blob: URL, i.e. the BROWSER's own PDF
 * viewer. Its dark toolbar (page, zoom, rotate, text, signature, undo/redo
 * and, in newer Chrome, "Summarize") has a fixed minimum width we cannot
 * style: in a narrow fax pane it ran past the pane, Print and Download sat off
 * to the right and the sideways scrollbar only showed at the bottom of the
 * PDF (staff report 10-22).
 *
 * Now our own toolbar sits ABOVE the scrolling pages (never inside them) and
 * fits the PANE, not the window: a ResizeObserver measures the viewer, and
 * below a width the less used controls move into a "⋯" menu. Print and
 * Download are always shown, pinned at the right end. "Open in a new tab"
 * gives the browser's full viewer (annotate, sign, its own tools).
 *
 * The pages are drawn by pdf.js (FaxPdfPages.jsx, loaded lazily); if that
 * cannot load or read the file, the browser's viewer is shown as before.
 */

// pdf.js only when a fax is shown; a failed chunk falls back to the browser viewer
const FaxPdfPages = lazy(() => import("./FaxPdfPages").catch(() => ({ default: NativeFallback })));

function NativeFallback({ url }) {
  return <iframe src={url} title="Fax Document" className="h-full w-full rounded-md border border-[#E5E7EB]" />;
}

// A render error in pdf.js shows the browser viewer instead of a blank pane.
class PagesBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(e) {
    if (typeof this.props.onError === "function") this.props.onError(e);
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
// pane widths (px) at which the toolbar sheds controls into "⋯"
export const VIEWER_COMPACT_BELOW = 640;
export const VIEWER_TINY_BELOW = 440;

export function viewerLayout(width) {
  if (!width || width >= VIEWER_COMPACT_BELOW) return "full";
  return width >= VIEWER_TINY_BELOW ? "compact" : "tiny";
}

function ToolButton({ label, onClick, disabled, children, showLabel = false, testid, active = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testid}
      className={`inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-1.5 text-[13px] text-[#344054] hover:bg-[#EAECF0] disabled:opacity-40 disabled:hover:bg-transparent ${
        active ? "bg-[#EAECF0]" : ""
      }`}
    >
      {children}
      {showLabel && <span className="whitespace-nowrap">{label}</span>}
    </button>
  );
}

const iconSx = { fontSize: 18 };

export default function FaxPdfViewer({ url, fileName = "fax.pdf" }) {
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const menuRef = useRef(null);
  const printFrameRef = useRef(null);
  const [paneWidth, setPaneWidth] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [nativeOnly, setNativeOnly] = useState(false);

  // a new fax starts at page 1, fit to width, upright, with pdf.js again
  useEffect(() => {
    setNumPages(0);
    setPage(1);
    setZoom(1);
    setRotate(0);
    setMenuOpen(false);
    setNativeOnly(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [url]);

  // Fit to the PANE (it changes with the fax list column, not only the window).
  useEffect(() => {
    const root = rootRef.current;
    const scroller = scrollRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    let timer = null;
    const measure = () => {
      setPaneWidth(Math.round(root.getBoundingClientRect().width));
      if (scroller) setPageWidth(Math.max(120, Math.floor(scroller.clientWidth - 16)));
    };
    measure();
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, 80);
    });
    ro.observe(root);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  // the "⋯" menu closes on a click outside it or Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // the print frame goes with the viewer
  useEffect(
    () => () => {
      if (printFrameRef.current) printFrameRef.current.remove();
      printFrameRef.current = null;
    },
    []
  );

  const onScroll = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const mid = scroller.scrollTop + scroller.clientHeight / 3;
    let current = 1;
    scroller.querySelectorAll("[data-fax-page]").forEach((el) => {
      if (el.offsetTop <= mid) current = Number(el.getAttribute("data-fax-page")) || current;
    });
    setPage((p) => (p === current ? p : current));
  }, []);

  const goTo = (n) => {
    const target = Math.min(Math.max(1, n), numPages || 1);
    const el = scrollRef.current?.querySelector(`[data-fax-page="${target}"]`);
    if (el && scrollRef.current) scrollRef.current.scrollTop = el.offsetTop - 8;
    setPage(target);
  };

  const zoomBy = (dir) => {
    setZoom((z) => {
      const i = ZOOMS.findIndex((v) => v >= z - 0.001);
      const next = ZOOMS[Math.min(Math.max(0, (i < 0 ? ZOOMS.length - 1 : i) + dir), ZOOMS.length - 1)];
      return next;
    });
  };

  const fitWidth = () => setZoom(1);
  const rotateRight = () => setRotate((r) => (r + 90) % 360);
  const openTab = () => url && window.open(url, "_blank", "noopener");

  const download = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Print the PDF itself (the browser prints it page for page) from a hidden
  // frame; if the browser will not, open it in a tab to print from there.
  const print = () => {
    if (!url) return;
    if (printFrameRef.current) printFrameRef.current.remove();
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.title = "Print fax";
    Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
    frame.onload = () => {
      setTimeout(() => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
        } catch (e) {
          openTab();
        }
      }, 250);
    };
    frame.src = url;
    document.body.appendChild(frame);
    printFrameRef.current = frame;
  };

  const layout = viewerLayout(paneWidth);
  const full = layout === "full";
  const tiny = layout === "tiny";
  const drawWidth = Math.round((pageWidth || 600) * zoom);
  const near = (n) => Math.abs(n - page) <= 2;

  const menuItems = [
    ...(tiny
      ? [
          { key: "zin", label: "Zoom in", icon: <ZoomInIcon sx={iconSx} />, onClick: () => zoomBy(1), disabled: zoom >= ZOOMS[ZOOMS.length - 1] },
          { key: "zout", label: "Zoom out", icon: <ZoomOutIcon sx={iconSx} />, onClick: () => zoomBy(-1), disabled: zoom <= ZOOMS[0] },
        ]
      : []),
    ...(!full
      ? [
          { key: "fit", label: "Fit to width", icon: <FitScreenIcon sx={iconSx} />, onClick: fitWidth },
          { key: "rot", label: "Rotate", icon: <RotateRightIcon sx={iconSx} />, onClick: rotateRight },
          { key: "tab", label: "Open in a new tab (annotate, sign)", icon: <OpenInNewIcon sx={iconSx} />, onClick: openTab },
        ]
      : []),
  ];

  return (
    <div
      ref={rootRef}
      className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg bg-[#F3F4F6]"
      data-testid="fax-document"
      data-src={url}
      data-layout={layout}
    >
      {/* Toolbar: outside the scrolling pages, never wider than the pane */}
      <div
        role="toolbar"
        aria-label="Fax document"
        className="flex min-h-[36px] w-full min-w-0 shrink-0 items-center gap-1 border-b border-[#E5E7EB] bg-white px-1.5 py-1"
        data-testid="fax-viewer-toolbar"
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5">
          {!nativeOnly && (
            <>
              <ToolButton label="Previous page" onClick={() => goTo(page - 1)} disabled={page <= 1}>
                <ChevronLeftIcon sx={iconSx} />
              </ToolButton>
              <span className="shrink-0 whitespace-nowrap px-0.5 text-[13px] tabular-nums text-[#344054]" data-testid="fax-viewer-page">
                {numPages ? `${page} / ${numPages}` : "– / –"}
              </span>
              <ToolButton label="Next page" onClick={() => goTo(page + 1)} disabled={!numPages || page >= numPages}>
                <ChevronRightIcon sx={iconSx} />
              </ToolButton>
              {!tiny && (
                <>
                  <span className="mx-1 h-4 w-px shrink-0 bg-[#E5E7EB]" />
                  <ToolButton label="Zoom out" onClick={() => zoomBy(-1)} disabled={zoom <= ZOOMS[0]}>
                    <ZoomOutIcon sx={iconSx} />
                  </ToolButton>
                  {full && (
                    <span className="w-10 shrink-0 text-center text-[12px] tabular-nums text-[#667085]">{Math.round(zoom * 100)}%</span>
                  )}
                  <ToolButton label="Zoom in" onClick={() => zoomBy(1)} disabled={zoom >= ZOOMS[ZOOMS.length - 1]}>
                    <ZoomInIcon sx={iconSx} />
                  </ToolButton>
                </>
              )}
              {full && (
                <>
                  <ToolButton label="Fit to width" onClick={fitWidth} active={zoom === 1}>
                    <FitScreenIcon sx={iconSx} />
                  </ToolButton>
                  <ToolButton label="Rotate" onClick={rotateRight}>
                    <RotateRightIcon sx={iconSx} />
                  </ToolButton>
                </>
              )}
            </>
          )}
        </div>

        {/* Pinned right: "⋯", Open, Print, Download, always visible */}
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {!nativeOnly && menuItems.length > 0 && (
            <div className="relative" ref={menuRef}>
              <ToolButton label="More" onClick={() => setMenuOpen((o) => !o)} testid="fax-viewer-more">
                <MoreHorizIcon sx={iconSx} />
              </ToolButton>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-8 z-20 min-w-[200px] rounded-md border border-[#E5E7EB] bg-white py-1 text-start shadow-lg"
                  data-testid="fax-viewer-menu"
                >
                  {menuItems.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      role="menuitem"
                      disabled={m.disabled}
                      onClick={() => {
                        setMenuOpen(false);
                        m.onClick();
                      }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-start text-[13px] text-[#344054] hover:bg-[#F2F4F7] disabled:opacity-40"
                    >
                      {m.icon}
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {(full || nativeOnly) && (
            <ToolButton label="Open in a new tab (annotate, sign)" onClick={openTab} disabled={!url}>
              <OpenInNewIcon sx={iconSx} />
            </ToolButton>
          )}
          <ToolButton label="Print" onClick={print} disabled={!url} showLabel={full} testid="fax-viewer-print">
            <PrintIcon sx={iconSx} />
          </ToolButton>
          <ToolButton label="Download" onClick={download} disabled={!url} showLabel={full} testid="fax-viewer-download">
            <FileDownloadOutlinedIcon sx={iconSx} />
          </ToolButton>
        </div>
      </div>

      {/* The pages scroll here, below the toolbar */}
      <div ref={scrollRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-auto p-2">
        {url &&
          (nativeOnly ? (
            <NativeFallback url={url} />
          ) : (
            <PagesBoundary key={url} fallback={<NativeFallback url={url} />} onError={() => setNativeOnly(true)}>
              <Suspense fallback={<div className="py-10 text-center text-xs text-[#98A2B3]">Loading the fax…</div>}>
                <FaxPdfPages
                  url={url}
                  width={drawWidth}
                  rotate={rotate}
                  near={near}
                  onPages={setNumPages}
                  onError={() => setNativeOnly(true)}
                />
              </Suspense>
            </PagesBoundary>
          ))}
      </div>
    </div>
  );
}
